import invariant from "tiny-invariant";
import JSBI from "jsbi";
import { Fraction, MaxUint256 } from "@uniswap/sdk-core";
import { Currency } from "../../models/financial/currency.js";
import { rawAmountToDecimalString } from "../../utils/format_utils.js";
import { notEmpty } from "../../utils/utils.js";
/**
 * A CurrencyAmount represents a fractional amount of a currency.
 * This class is inspired by the Uniswap SDK's (@uniswap/sdk-core) CurrencyAmount class.
 */
export class CurrencyAmount extends Fraction {
    /**
     * Returns a new currency amount instance from the unitless amount of token, i.e. the raw amount
     * @param currency the currency in the amount
     * @param rawAmount the raw token or ether amount
     * @param amountInFiatCurrency optional fiat currency amount representation
     */
    static fromRawAmount(currency, rawAmount, amountInFiatCurrency) {
        return new CurrencyAmount(currency, rawAmount, undefined, amountInFiatCurrency);
    }
    static toCurrencyAmount(amount) {
        if (!amount) {
            return undefined;
        }
        const currency = Currency.toCurrency(amount.currency);
        return CurrencyAmount.fromRawAmount(currency, amount.value);
    }
    /**
     * Scales a raw amount from one decimal representation to another.
     * @param rawAmount The raw amount in the source decimal format
     * @param fromCurrency The currency of the source amount
     * @param toCurrency The currency of the target amount
     * @param toDecimals The decimal places of the target amount
     * @returns The raw amount scaled to the target decimal format
     */
    static scaleRawAmount(rawAmount, fromCurrency, toCurrency) {
        const decimalDiff = fromCurrency.decimals - toCurrency.decimals;
        if (decimalDiff === 0) {
            return BigInt(rawAmount);
        }
        if (decimalDiff > 0) {
            return BigInt(rawAmount) / BigInt(10 ** decimalDiff);
        }
        return BigInt(rawAmount) * BigInt(10 ** -decimalDiff);
    }
    /**
     * `10^scale` for native bigint fixed-point math (cached). Used by the ledger
     * and other paths that work in scaled integers without a `Currency` instance.
     */
    static pow10(scale) {
        const cached = CurrencyAmount.SCALED_POW10_CACHE.get(scale);
        if (notEmpty(cached)) {
            return cached;
        }
        let value = BigInt(1);
        for (let i = 0; i < scale; i++) {
            value *= BigInt(10);
        }
        CurrencyAmount.SCALED_POW10_CACHE.set(scale, value);
        return value;
    }
    static absBigInt(value) {
        return value < BigInt(0) ? value * BigInt(-1) : value;
    }
    static sameSign(left, right) {
        if (left === BigInt(0) || right === BigInt(0)) {
            return true;
        }
        return (left > BigInt(0) && right > BigInt(0)) || (left < BigInt(0) && right < BigInt(0));
    }
    /**
     * Parse a decimal string to a scaled integer with HALF_UP rounding.
     */
    static parseDecimalToScaledBigInt(value, scale) {
        const trimmed = value?.trim();
        if (!notEmpty(trimmed)) {
            return undefined;
        }
        const matches = /^([+-])?(\d+)(?:\.(\d+))?$/.exec(trimmed);
        if (!notEmpty(matches)) {
            return undefined;
        }
        const sign = matches[1] === "-" ? BigInt(-1) : BigInt(1);
        const integerPart = matches[2] ?? "0";
        const fractionPart = matches[3] ?? "";
        const denominator = CurrencyAmount.pow10(scale);
        const integerScaled = BigInt(integerPart) * denominator;
        const paddedFraction = fractionPart.length >= scale ? fractionPart.slice(0, scale) : fractionPart.padEnd(scale, "0");
        const fractionScaled = paddedFraction.length > 0 ? BigInt(paddedFraction) : BigInt(0);
        let scaled = integerScaled + fractionScaled;
        if (fractionPart.length > scale) {
            const nextDigit = fractionPart[scale];
            if (nextDigit >= "5") {
                scaled += BigInt(1);
            }
        }
        return scaled * sign;
    }
    static formatScaledBigInt(value, scale) {
        const denominator = CurrencyAmount.pow10(scale);
        const negative = value < BigInt(0);
        const absolute = CurrencyAmount.absBigInt(value);
        const integerPart = absolute / denominator;
        const fractional = absolute % denominator;
        const rawFraction = fractional.toString().padStart(scale, "0");
        const trimmedFraction = rawFraction.replace(/0+$/, "");
        const sign = negative ? "-" : "";
        if (trimmedFraction.length === 0) {
            return `${sign}${integerPart.toString()}`;
        }
        return `${sign}${integerPart.toString()}.${trimmedFraction}`;
    }
    /**
     * Divide two integers and return a scaled integer (HALF_UP rounding).
     */
    static divideToScaled(numerator, denominator, scale) {
        if (denominator === BigInt(0)) {
            return BigInt(0);
        }
        const scaledNumerator = numerator * CurrencyAmount.pow10(scale);
        const negative = !CurrencyAmount.sameSign(scaledNumerator, denominator);
        const absoluteNumerator = CurrencyAmount.absBigInt(scaledNumerator);
        const absoluteDenominator = CurrencyAmount.absBigInt(denominator);
        const rounded = (absoluteNumerator + absoluteDenominator / BigInt(2)) / absoluteDenominator;
        return negative ? rounded * BigInt(-1) : rounded;
    }
    static decimalToScaled(value, scale) {
        const parsed = CurrencyAmount.parseDecimalToScaledBigInt(value, scale);
        if (!notEmpty(parsed)) {
            throw new Error(`invalid decimal value: ${value}`);
        }
        return parsed;
    }
    /**
     * Parse a decimal string to a scaled integer, returning 0 when the input
     * cannot be parsed. For ingest where a failed parse is recoverable.
     */
    static decimalToScaledOrZero(value, scale) {
        if (!notEmpty(value)) {
            return BigInt(0);
        }
        return CurrencyAmount.parseDecimalToScaledBigInt(value, scale) ?? BigInt(0);
    }
    /**
     * Fixed-precision inverse of `decimalToScaled`: always emits exactly `scale`
     * fractional digits (zero-padded), unlike `formatScaledBigInt` which trims
     * trailing zeros. For Decimal(N, scale) snapshot payloads (e.g. ClickHouse).
     */
    static formatScaledFixed(value, scale) {
        const denominator = CurrencyAmount.pow10(scale);
        const negative = value < BigInt(0);
        const absolute = CurrencyAmount.absBigInt(value);
        const integerPart = absolute / denominator;
        const fractional = (absolute % denominator).toString().padStart(scale, "0");
        const sign = negative ? "-" : "";
        return `${sign}${integerPart.toString()}.${fractional}`;
    }
    static toCanonicalDecimalString(value, decimals) {
        if (!notEmpty(value))
            return undefined;
        const raw = String(value).trim();
        if (raw.length === 0)
            return undefined;
        const normalized = CurrencyAmount.normalizeNumericString(raw);
        if (!notEmpty(normalized))
            return undefined;
        const scaled = CurrencyAmount.parseCanonicalDecimalToScaledBigInt(normalized, decimals);
        if (!notEmpty(scaled))
            return undefined;
        return CurrencyAmount.formatCanonicalScaledBigIntToDecimalString(scaled, decimals);
    }
    static multiplyCanonicalDecimalStrings(left, right, decimals) {
        const leftScaled = CurrencyAmount.parseCanonicalDecimalToScaledBigInt(left, decimals);
        const rightScaled = CurrencyAmount.parseCanonicalDecimalToScaledBigInt(right, decimals);
        if (!notEmpty(leftScaled) || !notEmpty(rightScaled))
            return undefined;
        const multipliedScaled = (leftScaled * rightScaled) / CurrencyAmount.pow10(decimals);
        return CurrencyAmount.formatCanonicalScaledBigIntToDecimalString(multipliedScaled, decimals);
    }
    static subtractCanonicalDecimalStrings(left, right, decimals) {
        const leftScaled = CurrencyAmount.parseCanonicalDecimalToScaledBigInt(left, decimals);
        const rightScaled = CurrencyAmount.parseCanonicalDecimalToScaledBigInt(right, decimals);
        if (!notEmpty(leftScaled) || !notEmpty(rightScaled))
            return undefined;
        return CurrencyAmount.formatCanonicalScaledBigIntToDecimalString(leftScaled - rightScaled, decimals);
    }
    static parseCanonicalDecimalToScaledBigInt(value, decimals) {
        const trimmed = value.trim();
        const decimalRegex = /^(-)?(\d+)(\.(\d+))?$/;
        const match = decimalRegex.exec(trimmed);
        if (!notEmpty(match))
            return undefined;
        const isNegative = match[1] === "-";
        const integerPart = match[2];
        const fractionalPart = match[4] ?? "";
        const paddedFractional = `${fractionalPart}${"0".repeat(decimals)}`.slice(0, decimals);
        const unsignedScaled = BigInt(integerPart) * CurrencyAmount.pow10(decimals) + BigInt(paddedFractional);
        return isNegative ? unsignedScaled * BigInt(-1) : unsignedScaled;
    }
    static formatCanonicalScaledBigIntToDecimalString(value, decimals) {
        const isNegative = value < BigInt(0);
        const unsigned = isNegative ? value * BigInt(-1) : value;
        const integerPart = unsigned / CurrencyAmount.pow10(decimals);
        const fractionalPart = unsigned % CurrencyAmount.pow10(decimals);
        const fractionalPadded = fractionalPart.toString().padStart(decimals, "0");
        const sign = isNegative ? "-" : "";
        return `${sign}${integerPart.toString()}.${fractionalPadded}`;
    }
    static normalizeNumericString(raw) {
        const plainDecimalRegex = /^-?\d+(\.\d+)?$/;
        if (plainDecimalRegex.test(raw))
            return raw;
        const scientificRegex = /^-?\d+(\.\d+)?[eE][+-]?\d+$/;
        if (!scientificRegex.test(raw))
            return undefined;
        return CurrencyAmount.expandScientificNotation(raw);
    }
    static expandScientificNotation(raw) {
        const match = /^(-)?(\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/.exec(raw);
        if (!notEmpty(match))
            return undefined;
        const sign = match[1] === "-" ? "-" : "";
        const integerPart = match[2];
        const fractionalPart = match[3] ?? "";
        const exponent = Number.parseInt(match[4], 10);
        if (!Number.isFinite(exponent))
            return undefined;
        const digits = `${integerPart}${fractionalPart}`;
        const decimalIndex = integerPart.length + exponent;
        let expanded;
        if (decimalIndex <= 0) {
            expanded = `0.${"0".repeat(Math.abs(decimalIndex))}${digits}`;
        }
        else if (decimalIndex >= digits.length) {
            expanded = `${digits}${"0".repeat(decimalIndex - digits.length)}`;
        }
        else {
            expanded = `${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
        }
        const normalized = expanded
            .replace(/^0+(?=\d)/, "")
            .replace(/^\./, "0.")
            .replace(/(\.\d*?)0+$/, "$1")
            .replace(/\.$/, "");
        return `${sign}${normalized}`;
    }
    /**
     * Construct a currency amount with a denominator that is not equal to 1
     * @param currency the currency
     * @param numerator the numerator of the fractional token amount
     * @param denominator the denominator of the fractional token amount
     */
    static fromFractionalAmount(currency, numerator, denominator) {
        return new CurrencyAmount(currency, numerator, denominator);
    }
    constructor(currency, numerator, denominator, amountInFiatCurrency) {
        super(numerator.toString(), denominator?.toString());
        invariant(JSBI.lessThanOrEqual(this.quotient, MaxUint256), "AMOUNT");
        this.currency = currency;
        this.decimalScale = JSBI.exponentiate(JSBI.BigInt(10), JSBI.BigInt(currency.decimals));
        this.amountInFiatCurrency = amountInFiatCurrency;
    }
    add(other) {
        if (!other) {
            return this;
        }
        invariant(this.currency.equals(other.currency), "CURRENCY");
        const added = super.add(other);
        return CurrencyAmount.fromFractionalAmount(this.currency, added.numerator, added.denominator);
    }
    subtract(other) {
        invariant(this.currency.equals(other.currency), "CURRENCY");
        const subtracted = super.subtract(other);
        return CurrencyAmount.fromFractionalAmount(this.currency, subtracted.numerator, subtracted.denominator);
    }
    multiply(other) {
        const multiplied = super.multiply(other);
        return CurrencyAmount.fromFractionalAmount(this.currency, multiplied.numerator, multiplied.denominator);
    }
    divide(other) {
        const divided = super.divide(other);
        return CurrencyAmount.fromFractionalAmount(this.currency, divided.numerator, divided.denominator);
    }
    cloneWithAmount(amount) {
        return CurrencyAmount.fromRawAmount(this.currency, amount);
    }
    toRawAmount() {
        return BigInt(this.quotient.toString());
    }
    /**
     * Override toFixed to account for currency decimals.
     * Divides the raw amount by 10^decimals before formatting.
     * e.g., raw amount 1000000 with 6 decimals returns "1.00" with decimalPlaces=2
     * Handles negative amounts so fractional part is never negative (avoids NaN).
     */
    toFixed(decimalPlaces = this.currency.decimals) {
        const rawAmount = this.toRawAmount();
        const divisor = BigInt(10) ** BigInt(this.currency.decimals);
        const sign = rawAmount < BigInt(0) ? "-" : "";
        const absRaw = rawAmount < BigInt(0) ? -rawAmount : rawAmount;
        const wholePart = absRaw / divisor;
        const fractionalPart = absRaw % divisor;
        // Convert fractional part to decimal string with proper padding
        const fractionalStr = fractionalPart.toString().padStart(this.currency.decimals, "0");
        // Combine and format to requested decimal places
        const fullNumber = sign + `${wholePart}.${fractionalStr}`;
        return Number(fullNumber).toFixed(decimalPlaces);
    }
    /**
     * Override toSignificant to account for currency decimals.
     * Handles negative amounts so fractional part is never negative (avoids NaN).
     */
    toSignificant(significantDigits = 6) {
        const rawAmount = this.toRawAmount();
        const divisor = BigInt(10) ** BigInt(this.currency.decimals);
        const sign = rawAmount < BigInt(0) ? "-" : "";
        const absRaw = rawAmount < BigInt(0) ? -rawAmount : rawAmount;
        const wholePart = absRaw / divisor;
        const fractionalPart = absRaw % divisor;
        const fractionalStr = fractionalPart.toString().padStart(this.currency.decimals, "0");
        const fullNumber = sign + `${wholePart}.${fractionalStr}`;
        return Number(fullNumber).toPrecision(significantDigits);
    }
    /**
     * Returns the amount as a decimal string, accounting for the currency's decimals.
     * Uses string manipulation to avoid precision loss with large numbers.
     * e.g., raw amount 1000000 with 6 decimals returns "1.000000"
     * @param decimalPlaces Number of decimal places to show (defaults to currency decimals)
     */
    toDecimalString(decimalPlaces) {
        return rawAmountToDecimalString(this.toRawAmount(), this.currency.decimals, decimalPlaces);
    }
    toCurrencyValue(price) {
        if (!price) {
            return this.amountInFiatCurrency;
        }
        const fraction = price.multiply(this);
        const denominator = JSBI.multiply(fraction.denominator, this.decimalScale);
        this.amountInFiatCurrency = CurrencyAmount.fromFractionalAmount(price.currency, fraction.numerator, denominator);
        return this.amountInFiatCurrency;
    }
    fiatValue() {
        return this.amountInFiatCurrency;
    }
    /**
     * Converts the currency amount to a model representation for serialization
     */
    toCurrencyAmountModel() {
        return {
            currency: this.currency.toCurrencyModel(),
            value: this.toRawAmount(),
            amount: this.toRawAmount(),
            amountInFiatCurrency: this.amountInFiatCurrency?.toCurrencyAmountModel(),
        };
    }
}
CurrencyAmount.SCALED_POW10_CACHE = new Map();
